import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { getProgram, programs } from '../data/programs';
import { Button } from '../components/ui/Button';
import { Eyebrow } from '../components/ui/Eyebrow';
import { CtaBand } from '../components/ui/CtaBand';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
export function ProgramDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const program = slug ? getProgram(slug) : undefined;
  const {
    brand,
    text,
    programImage
  } = useBrand();
  useDocumentMeta({
    title: program ? `${program.name} — ${brand.name}` : `Programs — ${brand.name}`,
    description: program ? program.summary : `Hormone, metabolic, longevity and everyday care from ${brand.name}.`,
    image: program ? programImage(program.slug, program.image) : brand.images.hero
  });
  if (!program) {
    return <main className="bg-cream" data-id="element-520" data-mp="1z1rzn1747mvf">
        <div className="mx-auto max-w-shell px-6 py-32 md:px-8" data-id="element-521" data-mp="1z2uqxa8cnrbv">
          <Eyebrow className="text-brick" data-id="element-522" data-mp="1v0zrkb9zbo0q">Not found</Eyebrow>
          <h1 className="mt-4 max-w-2xl font-serif text-4xl leading-tight text-ink md:text-6xl" data-id="element-523" data-mp="1uyd93gljvo3q">
            We could not find that program.
          </h1>
          <Link to="/programs" className="mt-8 inline-flex items-center gap-2 text-sm text-brick hover:text-ink" data-id="element-524" data-mp="240wcc1t902qi">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-525" data-mp="2irvf2w7ejx26" />
            Back to all programs
          </Link>
        </div>
      </main>;
  }
  const related = programs.filter(item => item.slug !== program.slug).slice(0, 3);
  return <main data-id="element-526" data-mp="2mrvv2la5ggbc">
      <header className="bg-cream" data-id="element-527" data-mp="2mrdytonhm23f">
        <div className="mx-auto max-w-shell px-6 pt-12 md:px-8 md:pt-16" data-id="element-528" data-mp="2msfbfxk5iwn9">
          <Link to="/programs" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-150 ease-republic hover:text-ink" data-id="element-529" data-mp="xdqynv7nfcop">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-530" data-mp="2sgw2l12yhxlj" />
            All programs
          </Link>

          <div className="mt-10 grid gap-12 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16" data-id="element-531" data-mp="2xe5ow7v1tt55">
            <div data-id="element-532" data-mp="32dilvlfmwglg">
              <Eyebrow className="text-brick" data-id="element-533" data-mp="32cz2s1e8zm5u">{program.category}</Eyebrow>
              <h1 className="mt-4 font-serif text-[2.75rem] leading-[1.02] text-ink md:text-[4rem]" data-id="element-534" data-mp="36fdnkr1h8vzr">
                {program.name}
              </h1>
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-ink-soft" data-id="element-535" data-mp="3be6yqctakawr">
                {text(program.description)}
              </p>

              <dl className="rule mt-10 grid gap-6 pt-8 sm:grid-cols-3" data-id="element-536" data-mp="3l4rwjy155bem">
                <div data-id="element-537" data-mp="3l6mfj6suqtg2">
                  <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-538" data-mp="3q3cyc1w1k356">From</dt>
                  <dd className="mt-2 font-serif text-3xl text-ink" data-id="element-539" data-mp="3u5hnoofgidm0">
                    ${program.priceFrom}
                    <span className="ml-2 font-sans text-sm text-ink-muted" data-id="element-540" data-mp="2ydky5qchdje">{program.cadence}</span>
                  </dd>
                </div>
                <div data-id="element-541" data-mp="1niil51jzqbjw">
                  <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-542" data-mp="1nel36j95fq1l">
                    Eligibility
                  </dt>
                  <dd className="mt-2 text-sm leading-relaxed text-ink-soft" data-id="element-543" data-mp="1ehaqctgonsuk">
                    {program.eligibility}
                  </dd>
                </div>
                <div data-id="element-544" data-mp="267fhdayy7e4r">
                  <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-545" data-mp="267oysa5xgcyu">
                    First shipment
                  </dt>
                  <dd className="mt-2 text-sm leading-relaxed text-ink-soft" data-id="element-546" data-mp="227ydglz66p1p">
                    Within 48 hours of protocol approval
                  </dd>
                </div>
              </dl>

              <div className="mt-10 flex flex-wrap gap-4" data-id="element-547" data-mp="1sdy05wtqidhw">
                <Button to="/get-started" data-id="element-548" data-mp="2pwhkyzaeaxsq">
                  Start your intake
                  <ArrowRightIcon className="h-4 w-4" data-id="element-549" data-mp="2l46a51e49rle" />
                </Button>
                <Button to="/labs" variant="secondary" data-id="element-550" data-mp="24p6tz2945gc3">
                  See the lab panel
                </Button>
              </div>
            </div>

            <img src={programImage(program.slug, program.image)} alt={`${program.name} at ${brand.name}`} className="aspect-[4/5] w-full rounded-brand object-cover" data-id="element-551" data-mp="29p329douftxg" />
          </div>
        </div>
      </header>

      <section className="bg-cream-light" data-id="element-552" data-mp="2oco28yqf9d15">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-553" data-mp="2odpm9spf19v0">
          <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20" data-id="element-554" data-mp="2sdq47nbq436t">
            <div data-id="element-555" data-mp="2enwdt0kklauz">
              <h2 className="font-serif text-3xl leading-tight text-ink md:text-4xl" data-id="element-556" data-mp="2embkfe9zrxyp">
                What the membership includes
              </h2>
              <ul className="mt-8 flex flex-col gap-4" data-id="element-557" data-mp="2jk4pg3zk6lxe">
                {program.includes.map(item => <li key={item} className="flex items-start gap-3" data-id="element-558" data-mp="3c2s5v63m1cs5">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brick" strokeWidth={2} data-id="element-559" data-mp="3c439n5x22kj9" />
                    <span className="text-base leading-relaxed text-ink-soft" data-id="element-560" data-mp="3lfr85lk58eu6">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-561" data-mp="3v6c2uxwp4z7t">
              <h2 className="font-serif text-3xl leading-tight text-ink md:text-4xl" data-id="element-562" data-mp="3v6vlyhy31tnf">
                How the protocol runs
              </h2>
              <ol className="mt-8" data-id="element-563" data-mp="32iq1zxeus8on">
                {program.protocol.map((phase, index) => <li key={phase.title} className="rule grid gap-2 py-6 sm:grid-cols-[3rem_1fr]" data-id="element-564" data-mp="2yh4gjno96m2u" data-mpi={index}>
                    <span className="font-serif text-2xl text-brick" data-id="element-565" data-mp="3cgw0e51gawor">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div data-id="element-566" data-mp="37h16rtqajt00">
                      <h3 className="font-serif text-xl text-ink" data-id="element-567" data-mp="37jvvvyk921zr">{phase.title}</h3>
                      <p className="mt-2 max-w-xl text-base leading-relaxed text-ink-soft" data-id="element-568" data-mp="rotwkuwko09i">
                        {phase.detail}
                      </p>
                    </div>
                  </li>)}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream" data-id="element-569" data-mp="ys1u1kw7rsf0">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-570" data-mp="13t7n5jtsdhp2">
          <Eyebrow className="text-brick" data-id="element-571" data-mp="13qvb1napm1jx">Medications used</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-ink md:text-[2.75rem]" data-id="element-572" data-mp="18qpvb7zzbsw0">
            Your clinician selects from these, and names the pharmacy that fills it.
          </h2>
          <ul className="mt-12" data-id="element-573" data-mp="g3wtemausnkf">
            {program.medications.map(medication => <li key={medication.name} className="rule grid gap-2 py-6 md:grid-cols-3 md:items-baseline md:gap-8" data-id="element-574" data-mp="l1omhjn8ifq7">
                <h3 className="font-serif text-2xl text-ink" data-id="element-575" data-mp="3ojgm9k7m43yy">{medication.name}</h3>
                <p className="text-base text-ink-soft" data-id="element-576" data-mp="3ti9yzaf2rqy2">{medication.form}</p>
                <p className="text-base text-ink-muted" data-id="element-577" data-mp="3tg6yfwgaqff3">{medication.note}</p>
              </li>)}
          </ul>
          <p className="mt-8 max-w-2xl text-sm leading-relaxed text-ink-muted" data-id="element-578" data-mp="3gzo1kt3yof4u">
            Compounded medications are not FDA-approved products. They are prepared by US-licensed
            503A and 503B pharmacies and dispensed only against a clinician-written prescription.
          </p>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-579" data-mp="330eo41753z4k">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-20" data-id="element-580" data-mp="3317l4agara9i">
          <h2 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-581" data-mp="2y84yyndun33c">
            Other programs
          </h2>
          <ul className="mt-8 grid gap-8 md:grid-cols-3" data-id="element-582" data-mp="2tacz976yqcol">
            {related.map(item => <li key={item.slug} className="rule pt-6" data-id="element-583" data-mp="2octls9i3a3xp">
                <Link to={`/programs/${item.slug}`} className="group flex h-full flex-col" data-id="element-584" data-mp="2jc5jussbo2op">
                  <h3 className="font-serif text-2xl leading-snug text-ink transition-colors duration-150 ease-republic group-hover:text-brick" data-id="element-585" data-mp="2f9sggb11sgv3">
                    {item.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-soft" data-id="element-586" data-mp="c18lta15w6ip">{item.summary}</p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-6 text-sm text-brick" data-id="element-587" data-mp="3t9c2segl693t">
                    From ${item.priceFrom}
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-republic group-hover:translate-x-1" data-id="element-588" data-mp="20y8lpf4mz3j" />
                  </span>
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-589" data-mp="3aiuovg0exi2i" />
    </main>;
}